/**
 * 开发票
 */
import React, { useEffect, memo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Table } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import ContentBlock from '@/components/Reservation/ContentBlock';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkOut';
import { uuid } from '@/utils';
import { DATE_TIME_FORMAT } from '@/config';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import { getRoomIncomeDetail, getGoodsDetail } from '../../helper';
import { orderTypeMap, OrderTypeEnum } from '../../constant';
import styles from './index.module.scss';

export enum typeEnum {
  goods = 'goodsIncome', // 商品
  roomFee = 'roomIncome', // 房费
}

interface IConfig {
  type: typeEnum; // 类型
  orders: any[];
}

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps extends MapStateProps, MapDispatchProps, IConfig {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { type, unmountNode, orders } = props;

  const [loading, setLoading] = useState<boolean>(false);
  // 表格数据
  const [dataSource, setDataSource] = useState<any[]>([]);
  // 小计 invoiceAmt只有房费明细才有
  const [subtotal, setSubtotal] = useState<{ number: number; amount: number; invoiceAmt?: number }>(
    {
      number: 0,
      amount: 0,
      invoiceAmt: 0,
    },
  );

  const initRoomFeeData = async () => {
    setLoading(true);
    try {
      const { dataSource: data, subtotal: sub } = await getRoomIncomeDetail(orders);
      setDataSource(data);
      setSubtotal(sub);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const initGoodsFeeData = async () => {
    setLoading(true);
    try {
      const { dataSource: data, subtotal: sub } = await getGoodsDetail(orders);
      setDataSource(data);
      setSubtotal(sub);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // 数据初始化
  useEffect(() => {
    if (type === typeEnum.roomFee) initRoomFeeData();
    if (type === typeEnum.goods) initGoodsFeeData();
  }, []);

  // 取消开发票
  const handleEditCancel = () => {
    unmountNode();
  };

  // if (!isInit) return <PortalLoading />;

  const goodsColumns: any[] = [
    {
      title: intl.get('bill_invoice_modal_order_type'),
      dataIndex: 'orderType',
      render: (val) => {
        return orderTypeMap[val] || '-';
      },
    },
    {
      title: intl.get('bill_invoice_modal_order_no'),
      dataIndex: 'orderNo',
    },
    {
      title: intl.get('bill_invoice_modal_consume_time'),
      dataIndex: 'postTime',
      render: (val) => {
        if (!val) return '-';
        return getTimeByUtcOffset(val, DATE_TIME_FORMAT);
      },
    },
    {
      title: intl.get('bill_invoice_modal_goods'),
      dataIndex: 'name',
    },
    {
      title: intl.get('bill_invoice_modal_sku'),
      dataIndex: 'specification',
      render: (val) => {
        return val || '-';
      },
    },
    {
      title: intl.get('bill_invoice_modal_actual_price'),
      dataIndex: 'price',
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: intl.get('bill_invoice_modal_number'),
      dataIndex: 'qty',
      render: (val) => {
        if (val === null || val === undefined) return '-';
        return val;
      },
    },
    {
      title: intl.get('bill_invoice_modal_subtotal'),
      dataIndex: 'subtotal',
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
  ];

  const roomFeeColumns: any[] = [
    {
      title: intl.get('bill_invoice_modal_order_type'),
      dataIndex: 'orderType',
      render: (val) => {
        return orderTypeMap[val] || '-';
      },
    },
    {
      title: intl.get('bill_invoice_modal_order_no'),
      dataIndex: 'orderNo',
    },
    {
      title: intl.get('bill_invoice_modal_time'),
      dataIndex: 'createTime',
      render: (val) => {
        if (!val) return '-';
        return getTimeByUtcOffset(val, DATE_TIME_FORMAT);
      },
    },
    {
      title: intl.get('bill_invoice_modal_room_no'),
      dataIndex: 'roomNo',
      render: (val, record) => {
        if (record.orderType === OrderTypeEnum.receptionConsume) return '-';
        return val;
      },
    },
    {
      title: intl.get('bill_invoice_modal_master'),
      dataIndex: 'name',
      render: (val, record) => {
        if (record.orderType === OrderTypeEnum.receptionConsume) return '-';
        return val;
      },
    },
    {
      title: intl.get('bill_invoice_modal_live_time'),
      dataIndex: 'occDuration',
    },
    {
      title: intl.get('bill_invoice_modal_room_fee'),
      dataIndex: 'consumeAmt',
      render: (val, record) => {
        return parseFen2Currency(record?.reserveFlag ? -val : val);
      },
    },
  ];

  const renderTotalPrice = (price) => {
    return parseFen2Currency(price);
  };

  const renderBottomInfo = () => {
    if (type === typeEnum.goods)
      return (
        <>
          <div className={styles.bottomInfo}>
            <span className={styles.label1}>{intl.get('bill_invoice_modal_good_total')}</span>
            {subtotal.number ?? '-'}
          </div>
          <div className={styles.bottomInfo}>
            <span className={styles.label1}>{intl.get('bill_invoice_modal_good_subtotal')}</span>
            {renderTotalPrice(subtotal.amount)}
          </div>
        </>
      );

    return (
      <>
        <div className={styles.bottomInfo}>
          <span className={styles.label}>{intl.get('bill_invoice_modal_live_time_subtotal')}</span>
          {subtotal.number ?? '-'}
        </div>
        <div className={styles.bottomInfo}>
          <span className={styles.label}>{intl.get('bill_invoice_modal_room_fee_total')}</span>
          {renderTotalPrice(subtotal.amount)}
        </div>
        <div className={styles.bottomInfo}>
          <span className={styles.label}>
            {intl.get('bill_invoice_modal_already_invoice_total')}
          </span>
          {renderTotalPrice(subtotal.invoiceAmt)}
        </div>
      </>
    );
  };

  const drawerTitle =
    type === typeEnum.goods
      ? intl.get('bill_invoice_modal_good_detail')
      : intl.get('bill_invoice_modal_room_fee_detail');
  const columns = type === typeEnum.goods ? goodsColumns : roomFeeColumns;

  return (
    <>
      <DrawerHeader
        title={intl.get('bill_invoice_modal_invoice_detail')}
        onClose={handleEditCancel}
      />
      <div className={styles.pageContainer}>
        <ContentBlock title={drawerTitle} className={styles.block}>
          <Table
            rowKey={() => uuid()}
            columns={columns || []}
            dataSource={dataSource || []}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </ContentBlock>
        {renderBottomInfo()}
      </div>
      <DrawerFooter>
        <Button onClick={handleEditCancel}>{intl.get('bill_invoice_modal_close')}</Button>
      </DrawerFooter>
    </>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
